import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Plus, MoreVertical, Pencil, Trash2, Wrench, Search, ArrowUpDown, Download } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useSortableData } from '../hooks/useSortableData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper function to export CSV with auth token
const exportWithAuth = async (endpoint, filename) => {
  const token = localStorage.getItem('token');
  try {
    const response = await fetch(`${API}${endpoint}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    if (!response.ok) {
      throw new Error('Export failed');
    }
    const blob = await response.blob();
    
    // Detect Safari
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    
    // For IE/Edge legacy
    if (window.navigator && window.navigator.msSaveOrOpenBlob) {
      window.navigator.msSaveOrOpenBlob(blob, filename);
      return;
    }
    
    // For Safari - use FileReader to convert to data URL
    if (isSafari) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const link = document.createElement('a');
        link.href = reader.result;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          document.body.removeChild(link);
        }, 250);
      };
      reader.readAsDataURL(blob);
      return;
    }
    
    // For Chrome, Firefox, Edge (Chromium)
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }, 250);
  } catch (error) {
    console.error('Export error:', error);
    throw error;
  }
};

const statusOptions = [
  { value: 'available', label: 'Available', class: 'bg-emerald-100 text-emerald-700' },
  { value: 'in_use', label: 'In Use', class: 'bg-blue-100 text-blue-700' },
  { value: 'maintenance', label: 'Maintenance', class: 'bg-amber-100 text-amber-700' },
  { value: 'retired', label: 'Retired', class: 'bg-slate-100 text-slate-700' },
];

const Tools = () => {
  const [tools, setTools] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterLocation, setFilterLocation] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingTool, setEditingTool] = useState(null);
  const [toolToDelete, setToolToDelete] = useState(null);
  const { canManageTools } = useAuth();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    serial_number: '',
    location_id: '',
    status: 'available'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [toolsRes, locationsRes] = await Promise.all([
        axios.get(`${API}/tools`),
        axios.get(`${API}/locations`)
      ]);
      setTools(toolsRes.data);
      setLocations(locationsRes.data);
    } catch (error) {
      toast.error('Failed to load tools');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      serial_number: '',
      location_id: '',
      status: 'available'
    });
    setEditingTool(null);
  };

  const handleOpenDialog = (tool = null) => {
    if (tool) {
      setEditingTool(tool);
      setFormData({
        name: tool.name,
        description: tool.description || '',
        serial_number: tool.serial_number || '',
        location_id: tool.location_id,
        status: tool.status
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTool) {
        await axios.put(`${API}/tools/${editingTool.id}`, formData);
        toast.success('Tool updated successfully');
      } else {
        await axios.post(`${API}/tools`, formData);
        toast.success('Tool created successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save tool');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/tools/${toolToDelete.id}`);
      toast.success('Tool deleted successfully');
      setDeleteDialogOpen(false);
      setToolToDelete(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete tool');
    }
  };

  const getLocationName = (locationId) => {
    const location = locations.find(l => l.id === locationId);
    return location?.name || '-';
  };

  const getStatusBadge = (status) => {
    const option = statusOptions.find(o => o.value === status);
    return (
      <Badge className={`rounded-sm ${option?.class || ''}`}>
        {option?.label || status}
      </Badge>
    );
  };

  // Filter tools by search term, location and status
  const searchFilteredTools = tools.filter(tool => {
    const matchesSearch = tool.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (tool.serial_number || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesLocation = filterLocation === 'all' || tool.location_id === filterLocation;
    const matchesStatus = filterStatus === 'all' || tool.status === filterStatus;
    return matchesSearch && matchesLocation && matchesStatus;
  });

  // Apply sorting using the custom hook
  const { sortedData: filteredTools, sortBy, setSortBy, sortOptions } = useSortableData(searchFilteredTools);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="tools-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">Tools</h1>
          <p className="text-slate-500 mt-1">Track tools by location</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={async () => {
              const params = new URLSearchParams();
              if (searchTerm) params.append('search', searchTerm);
              if (filterLocation !== 'all') params.append('location_id', filterLocation);
              if (filterStatus !== 'all') params.append('status', filterStatus);
              try {
                await exportWithAuth(`/tools/export?${params.toString()}`, 'tools_export.csv');
                toast.success('Tools exported successfully');
              } catch (error) {
                toast.error('Failed to export tools');
              }
            }}
            className="rounded-sm"
            data-testid="export-tools-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          {canManageTools() && (
            <Button
              onClick={() => handleOpenDialog()}
              className="bg-slate-900 hover:bg-slate-800 rounded-sm"
              data-testid="add-tool-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Tool
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card className="border-slate-200 shadow-sm rounded-sm">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <Input
                placeholder="Search by name or serial number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 rounded-sm"
                data-testid="tools-search-input"
              />
            </div>
            <Select value={filterLocation} onValueChange={setFilterLocation}>
              <SelectTrigger className="w-full md:w-48 rounded-sm" data-testid="tool-location-filter">
                <SelectValue placeholder="Location" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Locations</SelectItem>
                {locations.map(loc => (
                  <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-full md:w-48 rounded-sm" data-testid="tool-status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {statusOptions.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-full md:w-40 rounded-sm" data-testid="sort-select">
                <ArrowUpDown className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                {sortOptions.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-sm rounded-sm">
        <CardContent className="p-0">
          {filteredTools.length > 0 ? (
            <Table className="data-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Serial Number</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Status</TableHead>
                  {canManageTools() && <TableHead className="w-12"></TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTools.map(tool => (
                  <TableRow key={tool.id} data-testid={`tool-row-${tool.id}`}>
                    <TableCell className="font-semibold text-slate-900">{tool.name}</TableCell>
                    <TableCell className="font-mono text-slate-600">{tool.serial_number || '-'}</TableCell>
                    <TableCell className="text-slate-600">{getLocationName(tool.location_id)}</TableCell>
                    <TableCell className="text-slate-600 max-w-xs truncate">
                      {tool.description || '-'}
                    </TableCell>
                    <TableCell>{getStatusBadge(tool.status)}</TableCell>
                    {canManageTools() && (
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleOpenDialog(tool)}>
                              <Pencil className="w-4 h-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-red-600"
                              onClick={() => {
                                setToolToDelete(tool);
                                setDeleteDialogOpen(true);
                              }}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-slate-400">
              <Wrench className="w-12 h-12 mb-4" />
              <p className="text-lg font-medium">No tools found</p>
              <p className="text-sm">Add your first tool to get started</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="tool-dialog">
          <DialogHeader>
            <DialogTitle className="font-bold">
              {editingTool ? 'Edit Tool' : 'Add New Tool'}
            </DialogTitle>
            <DialogDescription>
              {editingTool ? 'Update tool details' : 'Add a new tool to track'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="rounded-sm"
                data-testid="tool-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="serial_number">Serial Number</Label>
                <Input
                  id="serial_number"
                  value={formData.serial_number}
                  onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
                  className="rounded-sm font-mono"
                  data-testid="tool-serial-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="status">Status *</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                  required
                >
                  <SelectTrigger className="rounded-sm" data-testid="tool-status-select">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {statusOptions.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="location_id">Location *</Label>
              <Select
                value={formData.location_id}
                onValueChange={(value) => setFormData({ ...formData, location_id: value })}
                required
              >
                <SelectTrigger className="rounded-sm" data-testid="tool-location-select">
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map(loc => (
                    <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="rounded-sm"
                rows={3}
                data-testid="tool-description-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">
                Cancel
              </Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="tool-submit-btn">
                {editingTool ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="delete-tool-dialog">
          <DialogHeader>
            <DialogTitle className="font-bold text-red-600">Delete Tool</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{toolToDelete?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} className="rounded-sm">
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} className="rounded-sm" data-testid="confirm-delete-tool-btn">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Tools;
